import { mergeInstances } from "@clairejs/client";
import { diffData } from "@clairejs/core";
import { getText, Tlt } from "@clairejs/react";
import { Avatar, Form, Input, InputRef, Modal, notification } from "antd";
import { useRef, useState } from "react";

import { User } from "../../dto/models/user";

import { userActions } from "../../store/action/user";

import { useDispatch, useStore } from "../../hooks/system";
import { PhotoData, uploadPhotos } from "../../hooks/upload";

interface Props {
    onClose?: (success?: boolean) => void;
}
export default function ProfileSettings({ onClose }: Props) {
    const me = useStore((s) => s.me?.info);
    const uploadInputRef = useRef<InputRef>(null);
    const [progress, setProgress] = useState(false);

    const dispatch = useDispatch((s) => s.me?.info);
    const dispatchUser = useDispatch((s) => s.users);

    const [selectedPhoto, setSelectedPhoto] = useState<PhotoData>({
        uri: me?.avatar,
    });
    const [displayName, setDisplayName] = useState(me?.displayName);

    const uploadPhoto = async (file?: File) => {
        if (!file) return;

        setSelectedPhoto((photo) => ({ ...photo, uploadProgress: 0, file, uri: URL.createObjectURL(file) }));

        //-- upload
        const { operations } = await uploadPhotos({
            photos: [{ file, id: "1" }],
            onUpdate: (_, update) => {
                setSelectedPhoto((photo) => ({ ...photo, ...update }));
            },
        });

        await operations?.();
    };

    const update = async () => {
        try {
            setProgress(true);

            const data: Partial<User> = { displayName, avatar: selectedPhoto.objectKey || selectedPhoto.uri };

            const diff = diffData(me, data);

            if (diff && Object.entries(diff).length) {
                const result = await dispatch(userActions.updateCurrentUser(diff));

                if (result) {
                    dispatchUser(async (dispatch, store) => {
                        dispatch({
                            instances: mergeInstances(User, store()?.instances || [], [{ id: me?.id, ...result }]),
                        });
                    });
                }
            }

            notification.success({ message: getText(<Tlt template="Update success" />) });

            onClose?.(true);
        } finally {
            setProgress(false);
        }
    };

    return (
        <Modal
            title={getText(<Tlt template="Update my profile" />)}
            open={true}
            onOk={update}
            okButtonProps={{ loading: progress }}
            onCancel={() => onClose?.()}
            maskClosable={false}>
            <Form layout="vertical">
                <Form.Item label={getText(<Tlt template="Profile avatar" />)}>
                    <Input
                        disabled={progress}
                        ref={uploadInputRef}
                        style={{ width: 0, height: 0, padding: 0, visibility: "hidden" }}
                        type="file"
                        onChange={(e) => uploadPhoto(e.target.files?.[0])}
                    />
                    <Avatar
                        size={120}
                        src={selectedPhoto.uri}
                        onClick={() => uploadInputRef.current?.nativeElement?.click()}
                    />
                </Form.Item>
                <Form.Item label={getText(<Tlt template="Display name" />)}>
                    <Input
                        disabled={progress}
                        defaultValue={me?.displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
